import { ComponentType, useMemo } from 'react';
import { useLoginStore } from '@shared/store/useLoginStore';
import { createLoginFormSubmitHandler, getLoginFormCopy } from '@modules/login/business_logic';
import { useGuestSignIn } from '@modules/auth/hooks/useGuestSignIn';
import { useLoginSubmit } from '../hooks/useLoginSubmit';
import type { LoginProps } from '../views/login_view';

interface LoginContainerProps {
  View: ComponentType<LoginProps>;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onSuccessNavigate: () => void;
  onNavigateToSignUp: () => void;
  onNavigateToForgotPassword: () => void;
  /** When true, wires `onFormSubmit` for HTML form preventDefault (web). */
  useFormSubmitHandler?: boolean;
}

export function LoginContainer({
  View,
  onSuccess,
  onError,
  onSuccessNavigate,
  onNavigateToSignUp,
  onNavigateToForgotPassword,
  useFormSubmitHandler = false,
}: LoginContainerProps) {
  const isSubmitting = useLoginStore((s) => s.isSubmitting);
  const form = useLoginStore((s) => s.form);
  const setForm = useLoginStore((s) => s.setForm);
  const setIsSubmitting = useLoginStore((s) => s.setIsSubmitting);
  const reset = useLoginStore((s) => s.reset);
  const handleSubmit = useLoginSubmit(onSuccess, onError, onSuccessNavigate);
  const handleGuestSignIn = useGuestSignIn(
    onSuccess,
    onError,
    onSuccessNavigate,
    setIsSubmitting,
    reset
  );
  const handleEmailChange = (email: string) => setForm({ email });
  const handlePasswordChange = (password: string) => setForm({ password });

  const formCopy = useMemo(() => getLoginFormCopy(isSubmitting), [isSubmitting]);

  const onFormSubmit = useMemo(
    () => (useFormSubmitHandler ? createLoginFormSubmitHandler(handleSubmit) : undefined),
    [useFormSubmitHandler, handleSubmit]
  );

  return (
    <View
      {...formCopy}
      isSubmitting={isSubmitting}
      email={form.email}
      password={form.password}
      onEmailChange={handleEmailChange}
      onPasswordChange={handlePasswordChange}
      onSubmit={handleSubmit}
      onNavigateToSignUp={onNavigateToSignUp}
      onNavigateToForgotPassword={onNavigateToForgotPassword}
      onGuestSignIn={handleGuestSignIn}
      onFormSubmit={onFormSubmit}
    />
  );
}

export type { LoginProps };
